import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listUsers, updateUser, type AdminUser } from '../api/admin';

const ROLE_OPTIONS = ['member', 'admin', 'super_admin'] as const;

const roleBadge: Record<string, string> = {
  member: 'bg-slate-100 text-slate-600',
  admin: 'bg-blue-50 text-blue-700',
  super_admin: 'bg-purple-50 text-purple-700',
};

export function UsersPage() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState('');

  const usersQuery = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: listUsers,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { role?: string; is_active?: boolean } }) =>
      updateUser(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });

  const users = (usersQuery.data || []).filter(
    (u: AdminUser) =>
      !filter ||
      u.name.toLowerCase().includes(filter.toLowerCase()) ||
      u.email.toLowerCase().includes(filter.toLowerCase()) ||
      (u.org_name && u.org_name.toLowerCase().includes(filter.toLowerCase())),
  );

  // Count inactive users
  const inactiveCount = users.filter((u: AdminUser) => !u.is_active).length;

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900">User Management</h1>
        <p className="text-slate-600 mt-2">
          Manage all platform users, approve new registrations, and control access.
        </p>
      </div>

      {inactiveCount > 0 && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
          <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div>
            <div className="font-semibold text-amber-900">
              {inactiveCount} user{inactiveCount !== 1 ? 's' : ''} pending approval
            </div>
            <div className="text-sm text-amber-700 mt-1">
              New users cannot sign in until you activate their accounts
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-4 mb-6">
        <div className="flex-1">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search by name, email, or organization..."
            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
        <div className="text-sm text-slate-600">
          {users.length} user{users.length !== 1 ? 's' : ''}
        </div>
      </div>

      {usersQuery.isLoading && (
        <div className="text-center py-12 text-slate-400">Loading users...</div>
      )}

      {!usersQuery.isLoading && users.length === 0 && (
        <div className="text-center py-12 text-slate-400">No users found</div>
      )}

      {!usersQuery.isLoading && users.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                <th className="px-6 py-4">User</th>
                <th className="px-6 py-4">Organization</th>
                <th className="px-6 py-4">Role</th>
                <th className="px-6 py-4 text-center">Status</th>
                <th className="px-6 py-4">Joined</th>
                <th className="px-6 py-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((user: AdminUser) => (
                <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="font-medium text-slate-900">{user.name}</div>
                    <div className="text-slate-500 text-xs">{user.email}</div>
                  </td>
                  <td className="px-6 py-4 text-slate-600">{user.org_name || '—'}</td>
                  <td className="px-6 py-4">
                    <select
                      value={user.role}
                      onChange={(e) =>
                        updateMutation.mutate({
                          id: user.id,
                          payload: { role: e.target.value },
                        })
                      }
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg border-0 cursor-pointer ${
                        roleBadge[user.role] || roleBadge.member
                      }`}
                    >
                      {ROLE_OPTIONS.map((r) => (
                        <option key={r} value={r}>
                          {r.replace('_', ' ')}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                        user.is_active
                          ? 'bg-green-50 text-green-700'
                          : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          user.is_active ? 'bg-green-500' : 'bg-slate-400'
                        }`}
                      />
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-slate-500 text-xs">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() =>
                        updateMutation.mutate({
                          id: user.id,
                          payload: { is_active: !user.is_active },
                        })
                      }
                      disabled={updateMutation.isPending}
                      className={`px-4 py-2 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 ${
                        user.is_active
                          ? 'text-red-700 border border-red-200 hover:bg-red-50'
                          : 'text-green-700 border border-green-200 hover:bg-green-50 bg-green-50'
                      }`}
                    >
                      {user.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
